import { base64ToBytes, base64UrlToBytes, bytesToBase64, bytesToBase64Url } from './bytes.ts';
import { decryptUtf8, encryptUtf8, importAesKey } from './aes.ts';

export const DEFAULT_SHARE_COUNT = 4;
export const defaultHolderLabels = [
  'Havn Sparebank (proposal only)',
  'Stiftelsen Åpen Dør (proposal only)',
  'Friend A',
  'Friend B'
];

export type WrappedPrivateKey = {
  iv: string;
  ciphertext: string;
};

export type WrappedThreadKey = {
  userId: string;
  ephemeralPublicKeyJwk: JsonWebKey;
  iv: string;
  ciphertext: string;
};

export type IdentityBundle = {
  publicKeyJwk: JsonWebKey;
  privateKeyJwk: JsonWebKey;
  privateKey: CryptoKey;
  secretD: Uint8Array;
};

const ECDH_P256 = { name: 'ECDH', namedCurve: 'P-256' } as const;
const HKDF_INFO = 'farfar-thread-wrap-v1';
const HKDF_SALT = 'farfar-hkdf-salt-v1';

export async function generateIdentity(): Promise<IdentityBundle> {
  const pair = await crypto.subtle.generateKey(ECDH_P256, true, ['deriveBits']);
  const publicKeyJwk = await crypto.subtle.exportKey('jwk', pair.publicKey);
  const privateKeyJwk = await crypto.subtle.exportKey('jwk', pair.privateKey);
  if (!privateKeyJwk.d) throw new Error('Private key export missing d');
  return {
    publicKeyJwk,
    privateKeyJwk,
    privateKey: pair.privateKey,
    secretD: base64UrlToBytes(privateKeyJwk.d)
  };
}

export function privateJwkFromSecretD(publicKeyJwk: JsonWebKey, secretD: Uint8Array): JsonWebKey {
  return {
    kty: 'EC',
    crv: 'P-256',
    x: publicKeyJwk.x,
    y: publicKeyJwk.y,
    d: bytesToBase64Url(secretD),
    ext: true,
    key_ops: ['deriveBits']
  };
}

export async function importPrivateKey(jwk: JsonWebKey): Promise<CryptoKey> {
  const next = { ...jwk, key_ops: ['deriveBits'], ext: true };
  return crypto.subtle.importKey('jwk', next, ECDH_P256, true, ['deriveBits']);
}

export async function importPublicKey(jwk: JsonWebKey): Promise<CryptoKey> {
  const { d, key_ops, ...pub } = jwk;
  return crypto.subtle.importKey('jwk', { ...pub, key_ops: [] }, ECDH_P256, true, []);
}

export async function wrapPrivateKeyWithWrapKey(wrapKey: CryptoKey): Promise<{
  wrappedPrivateKey: WrappedPrivateKey;
  identity: IdentityBundle;
}> {
  const identity = await generateIdentity();
  const wrapped = await encryptUtf8(wrapKey, JSON.stringify(identity.privateKeyJwk));
  return { wrappedPrivateKey: wrapped, identity };
}

export async function rewrapPrivateJwk(wrapKey: CryptoKey, privateKeyJwk: JsonWebKey): Promise<WrappedPrivateKey> {
  return encryptUtf8(wrapKey, JSON.stringify(privateKeyJwk));
}

export async function unwrapPrivateKeyWithWrapKey(
  wrapKey: CryptoKey,
  wrapped: WrappedPrivateKey
): Promise<{ privateKey: CryptoKey; privateKeyJwk: JsonWebKey; secretD: Uint8Array }> {
  const json = await decryptUtf8(wrapKey, wrapped.iv, wrapped.ciphertext);
  const privateKeyJwk = JSON.parse(json) as JsonWebKey;
  if (!privateKeyJwk.d) throw new Error('Unwrapped key is missing d');
  const privateKey = await importPrivateKey(privateKeyJwk);
  return { privateKey, privateKeyJwk, secretD: base64UrlToBytes(privateKeyJwk.d) };
}

async function deriveWrapAesKey(privateKey: CryptoKey, publicKey: CryptoKey): Promise<CryptoKey> {
  const bits = await crypto.subtle.deriveBits({ name: 'ECDH', public: publicKey }, privateKey, 256);
  const hkdfBase = await crypto.subtle.importKey('raw', bits, 'HKDF', false, ['deriveKey']);
  return crypto.subtle.deriveKey(
    {
      name: 'HKDF',
      hash: 'SHA-256',
      salt: new TextEncoder().encode(HKDF_SALT),
      info: new TextEncoder().encode(HKDF_INFO),
    },
    hkdfBase,
    { name: 'AES-GCM', length: 256 },
    false,
    ['encrypt', 'decrypt']
  );
}

export async function wrapThreadKeyForUser(
  threadKeyBytes: Uint8Array,
  recipientUserId: string,
  recipientPublicJwk: JsonWebKey
): Promise<WrappedThreadKey> {
  const ephemeral = await crypto.subtle.generateKey(ECDH_P256, true, ['deriveBits']);
  const recipientPub = await importPublicKey(recipientPublicJwk);
  const wrapKey = await deriveWrapAesKey(ephemeral.privateKey, recipientPub);
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const ciphertext = await crypto.subtle.encrypt({ name: 'AES-GCM', iv }, wrapKey, threadKeyBytes);
  return {
    userId: String(recipientUserId),
    ephemeralPublicKeyJwk: await crypto.subtle.exportKey('jwk', ephemeral.publicKey),
    iv: bytesToBase64(iv),
    ciphertext: bytesToBase64(new Uint8Array(ciphertext))
  };
}

export async function unwrapThreadKey(
  privateKey: CryptoKey,
  wrapped: WrappedThreadKey
): Promise<Uint8Array> {
  const ephemeralPub = await importPublicKey(wrapped.ephemeralPublicKeyJwk);
  const wrapKey = await deriveWrapAesKey(privateKey, ephemeralPub);
  const plain = await crypto.subtle.decrypt(
    { name: 'AES-GCM', iv: base64ToBytes(wrapped.iv) },
    wrapKey,
    base64ToBytes(wrapped.ciphertext)
  );
  return new Uint8Array(plain);
}

export async function threadAesKey(threadKeyBytes: Uint8Array): Promise<CryptoKey> {
  return importAesKey(threadKeyBytes);
}
